import React, { Fragment, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
import './News.scss';
import NewsItem from '../../components/NewsItem/NewsItem';
import CommentSection from '../../components/CommentSection/CommentSection';
import { fetchAllNews, fetchAllNewsPaginate, filterNews, setAllNews } from '../../Reducers/actions/newsActions';
import { cachingService, invalidateNewsCache, invalidateViewerNewsCache } from '../../../helpers/cache';
import SearchOverlayNews from '../../components/Explore/SearchOverlayNews';
import { setSearchOverlayQueryNews, searchNews, exitSearchModeNews } from '../../Reducers/actions/newsActions';
import Icon from '../../components/Icon/Icon';
import FilterBarNews from '../../components/FilterBarNews/FilterBarNews';
import { saveScrollPosition } from '../../Reducers/actions/navigationActions';
import NewsItemSkeleton from '../../components/NewsItem/NewsItemSkeleton';
import { useSoftwall } from '../SoftWall/SoftWall';
import { usePullToRefresh } from '../../../hooks/usePullToRefresh';

// Import debounce utility
const debounce = (func, delay) => {
  let timeoutId;
  const debouncedFunc = function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };

  // Add cancel method for cleanup
  debouncedFunc.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFunc;
};

const MAX_AUTO_LOAD_ATTEMPTS = 3;

// Viewer the news feed was last shown to. Module scope so it survives unmounts:
// login/logout happens on other routes, and the feed's is_liked is per viewer (HYK-1111).
let newsFeedViewerId;

const News = () => {
  // const navigate = useNavigate();
  // const userId = useSelector(state => state.auth.user?.user_id);
  const searchOverlayOpen = useSelector(state => state.news.searchOverlayOpen);
  const searchBarFloating = useSelector(state => state.news.searchBarFloating);
  const searchOverlayQuery = useSelector(state => state.news.searchOverlayQuery);
  const searchResults = useSelector(state => state.news.searchResults || []);
  const searchLoading = useSelector(state => state.news.loading && searchBarFloating);
  const searchError = useSelector(state => state.news.error && searchBarFloating);
  const savedScrollPosition = useSelector(state => state.navigation?.scrollPositions?.news?.main || 0);
  const { allNews: news, error, loading,activeTab,user ,alnews} = useSelector(state => ({
    allNews: filterNews(state),
    error: state.news.error,
    loading: state.news.loading,
    activeTab: state.news.activeTab,
    user: state.auth.user,
    alnews:state.news.allNews,
  }));  
  const sw = useSoftwall();
  const dispatch = useDispatch();
  const [filteredNews, setFilteredNews] = useState(news);
  const [showComments, setShowComments] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [firstLoad, setFirstLoad] = useState(news && news.length > 0);

  useEffect(() => {
    setFilteredNews(filterNews({news:{allNews:alnews,activeTab:activeTab},auth:{user:user}}));
  }, [activeTab,alnews,user]);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const searchOverlayOpenRef = useRef(false);
  const searchBarFloatingRef = useRef(false);
  const scrollListenerRef = useRef(null);
  const scrollThrottleRef = useRef(false);
  const isInitializedRef = useRef(false);
  const autoLoadAttemptsRef = useRef(0);

  
  // Update refs when their corresponding state changes
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    searchOverlayOpenRef.current = searchOverlayOpen;
  }, [searchOverlayOpen]);

  useEffect(() => {
    searchBarFloatingRef.current = searchBarFloating;
  }, [searchBarFloating]);

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    try {
      // Clear cache to force fresh fetch - invalidate all news URLs
      // The cache service cleans keys, so we need to invalidate the path pattern
      invalidateNewsCache()      
      // Reset pagination and fetch fresh data
      setPage(1);
      pageRef.current = 1;
      setHasMore(true);
      hasMoreRef.current = true;
      isInitializedRef.current = false; 
      // Allow re-initialization
      await dispatch(fetchAllNews(1, 10));
    } catch (error) {
      console.error('Error refreshing news:', error);
    }
  }, [dispatch]);

  // Pull-to-refresh hook - disable when loading, searching, or search overlay is open
  const { containerRef, isRefreshing, pullDistance, pullProgress, shouldTrigger, shouldShowIndicator } = usePullToRefresh(
    handleRefresh,
    { 
      threshold: 80, 
      disabled: loading || isLoadingMore || searchOverlayOpen || searchBarFloating || !firstLoad 
    }
  );

  // Viewer changed (login/logout/account switch): drop the cached per-viewer feed
  // and refetch it, otherwise is_liked stays the previous viewer's (HYK-1111).
  // Runs before the init effect so an empty feed is fetched without stale cache.
  const viewerId = user?.user_id ?? null;
  useEffect(() => {
    const viewerChanged = newsFeedViewerId !== undefined && newsFeedViewerId !== viewerId;
    newsFeedViewerId = viewerId;
    if (!viewerChanged) return;
    invalidateViewerNewsCache();
    if (alnews?.length) {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerId]);

  // Initialize data only once
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      // Skip the fetch if news is already loaded
      if (!loading && !news?.length) {
        dispatch(fetchAllNews(1, 10)).then(() => {
          setFirstLoad(true);
        });
      } else if (news?.length > 0) {
        setFirstLoad(true);
      }
    }
  }, []);

  // Save scroll position before component unmounts (when navigating away)
  useEffect(() => {
    return () => {
      const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
      if (currentPosition > 0) {
        dispatch(saveScrollPosition('news', 'main', currentPosition));
      }
    };
  }, [dispatch]);

  // Restore scroll position after news data is loaded
  useEffect(() => {
    if (news && news.length > 0 && savedScrollPosition > 0) {

      // Calculate how many news items we need to load based on scroll position
      // Each news item is roughly 200px tall, so we can estimate how many items are needed
      const estimatedItemHeight = 200;
      const estimatedItemsNeeded = Math.ceil(savedScrollPosition / estimatedItemHeight);
      const currentItemsLoaded = news.length;

      // If we need more items, load them before restoring scroll position
      if (estimatedItemsNeeded > currentItemsLoaded && hasMore) {
        const pagesNeeded = Math.ceil(estimatedItemsNeeded / 10); // 10 items per page

        // Load additional pages
        const loadAdditionalPages = async () => {
          for (let i = 2; i <= pagesNeeded; i++) {
            try {
              const result = await dispatch(fetchAllNewsPaginate(i, 10));
              if (result?.response?.pagination) {
                setHasMore(i < result.response.pagination.totalPages);
              }
            } catch (error) {
              console.error('Error loading additional page:', error);
              break;
            }
          }

          // After loading all needed pages, restore scroll position
          setTimeout(() => {
            window.scrollTo(0, savedScrollPosition);
          }, 100);
        };

        loadAdditionalPages();
      } else {
        // Restore scroll position instantly if we have enough items
        window.scrollTo(0, savedScrollPosition);
      }
    }
  }, [news, savedScrollPosition, hasMore, dispatch]);

  // const handleAddNews = () => {
  //   navigate('/news/add');
  // };
  useEffect(() => {
    if (!['subscriptions', 'saved'].includes(activeTab)) {
      autoLoadAttemptsRef.current = 0;
      return;
    }

    if ((filteredNews && filteredNews.length > 0)) {
      autoLoadAttemptsRef.current = 0;
      return;
    }

    if (loading || isLoadingMoreRef.current || !hasMoreRef.current) {
      return;
    }

    if (autoLoadAttemptsRef.current >= MAX_AUTO_LOAD_ATTEMPTS) {
      return;
    }

    const nextPage = pageRef.current + 1;

    autoLoadAttemptsRef.current += 1;
    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;

    dispatch(fetchAllNewsPaginate(nextPage, 10)).then(({ response }) => {
      setPage(nextPage);
      pageRef.current = nextPage;
      const totalPages = response?.pagination?.totalPages;
      if (typeof totalPages === 'number') {
        const hasNext = nextPage < totalPages;
        setHasMore(hasNext);
        hasMoreRef.current = hasNext;
      } else if (!response?.data || response.data.length === 0) {
        setHasMore(false);
        hasMoreRef.current = false;
      }
    }).catch((error) => {
      console.error('Error loading additional news for filter:', error);
      setHasMore(false);
      hasMoreRef.current = false;
    }).finally(() => {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    });
  }, [activeTab, filteredNews, loading, dispatch]);

  const handleShowComments = useCallback((e, news) => {
    if (!sw.requireAuth("Comment on News","Sign up or log in to be able to comment on this news")) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setShowComments(true);
    setSelectedNews(news);
  }, []);

  // Floating search bar logic
  const handleFloatingChange = useCallback((e) => {
    const query = e.target.value;
    dispatch(setSearchOverlayQueryNews(query));

    // Trigger debounced search
  }, [dispatch, ]);

  const handleFloatingKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      if (searchOverlayQuery && searchOverlayQuery.length >= 2) {
        dispatch(searchNews(searchOverlayQuery));
      }
    }
  }, [searchOverlayQuery, dispatch]);

  const handleFloatingClose = useCallback((e) => {
    e.stopPropagation();
    dispatch(exitSearchModeNews());
  }, [dispatch]);

  const handleFloatingSearch = useCallback(() => {
    if (searchOverlayQuery && searchOverlayQuery.length >= 2) {
      dispatch(searchNews(searchOverlayQuery));
    }
  }, [searchOverlayQuery, dispatch]);

  // Handle scroll with throttling for better iOS performance
  const handleScroll = useCallback(() => {
    // Throttle scroll events to prevent excessive calls on iOS
    if (scrollThrottleRef.current) return;

    scrollThrottleRef.current = true;

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      scrollThrottleRef.current = false;

      if (searchOverlayOpenRef.current || searchBarFloatingRef.current || isLoadingMoreRef.current) return;

      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 1000;

      if (scrollPosition >= threshold && hasMoreRef.current) {
        setIsLoadingMore(true);
        const nextPage = pageRef.current + 1;
        setPage(nextPage);

        dispatch(fetchAllNewsPaginate(nextPage, 10)).then(({ response }) => {
          if (response?.pagination) {
            setHasMore(nextPage < response.pagination.totalPages);
          }
          setIsLoadingMore(false);
        }).catch(() => {
          setIsLoadingMore(false);
        });
      }
    });
  }, [dispatch]);

  // Add scroll listener with passive option for better iOS performance
  useEffect(() => {
    const currentScrollHandler = handleScroll;
    scrollListenerRef.current = currentScrollHandler;

    window.addEventListener('scroll', currentScrollHandler, { passive: true });

    return () => {
      window.removeEventListener('scroll', currentScrollHandler);
    };
  }, [handleScroll]);

  // Only show loading on initial load, not during pagination




  return (
    <div className="news mobile-root-offset" ref={containerRef}>
      {/* Pull-to-refresh indicator */}
      {(shouldShowIndicator || isRefreshing || shouldTrigger) && (
        <div 
          className="pull-to-refresh-indicator"
          style={{
            transform: `translateY(${Math.min(pullDistance, 1)}px)`,
            opacity: Math.min(pullProgress, 1)
          }}
        >
          <div className="pull-to-refresh-spinner">
            {isRefreshing ? (
              <div className="spinner" />
            ) : (
              <Icon 
                alias="chevron-down" 
                width={24} 
                color="#FF3232"
                style={{
                  transform: `rotate(${shouldTrigger ? 180 : 0}deg)`,
                  transition: 'transform 0.2s ease'
                }}
              />
            )}
          </div>
          <p className="pull-to-refresh-text">
            {isRefreshing ? 'Refreshing...' : shouldTrigger ? 'Release to refresh' : 'Pull to refresh'}
          </p>
        </div>
      )}
      
      {/* Only show overlay if open */}
      {searchOverlayOpen && <SearchOverlayNews />}
      {/* Floating search bar (fixed, overlay style, no background) */}
      {!searchOverlayOpen && searchBarFloating && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100dvw', zIndex: 2000 }}>
          <div className="search-overlay__bar" style={{ background: '#fff', borderBottom: '1px solid #e0e0e0' }}>
            <input
              type="text"
              className="search-overlay__input"
              placeholder="Search for news or author"
              value={searchOverlayQuery}
              onChange={handleFloatingChange}
              onKeyDown={handleFloatingKeyDown}
              autoFocus
            />
            <span onClick={handleFloatingSearch} style={{ cursor: 'pointer' }}>
              <Icon alias="search" width={20} color="#777" className="search-overlay__icon" />
            </span>
            <button className="search-overlay__close" onClick={handleFloatingClose}>
              <Icon alias="cross" width={20} color="#777" />
            </button>
          </div>
        </div>
      )}
      {/* Show search results if a search has run, otherwise show all news */}
      {!searchOverlayOpen && searchBarFloating ? (
        <div className="news__list">
          {searchLoading ? (
            <>
              {[1, 2, 3, 4].map((item) => (
                <NewsItemSkeleton key={item} />
              ))}
            </>
          ) : searchError ? (
            <div className="news__error">
              <p>Error searching news. Please try again later.</p>
              <p className="news__error-details">{searchError}</p>
            </div>
          ) : searchResults && searchResults.length > 0 && (
            searchResults.map((item) => (
              <NewsItem key={item.id} news={item} handleShowComments={handleShowComments} type='all' />
            ))
          )}
        </div>
      ) : (
        <Fragment>
          {showComments && <CommentSection
            type='news'
            creation_id={selectedNews.id}
            onClose={() => setShowComments(false)} />}

          {error && (
            <div className="news__error">
              <p>Error loading news. Please try again later.</p>
              {/* <p className="news__error-details">{error}</p> */}
            </div>
          )}

          <div className='news-tabs mx-16 '>
            <FilterBarNews />
          </div>

          <div className="news__list">
            {(loading && !filteredNews?.length) &&
              [1, 2, 3, 4].map((item) => <NewsItemSkeleton key={item} />)}
            {loading && (!filteredNews || filteredNews.length === 0) ? (
              [1, 2, 3, 4].map((item) => <NewsItemSkeleton key={item} />)

            ) : (
              filteredNews.map((item) => (
                <NewsItem key={item.id} news={item} handleShowComments={handleShowComments} type='all' />
              ))
            )}
          </div>

          {isLoadingMore && (
            [1, 2, 3, 4].map((item) => <NewsItemSkeleton key={item} />)
          )}

          {/* <button className="news__add" onClick={handleAddNews} aria-label="Add news">
          <i className="fas fa-plus"></i>
        </button> */}
        </Fragment>
      )}

    </div>
  );
};

export default News; 